import React, {
  forwardRef,
  ForwardedRef,
  ReactNode,
  InputHTMLAttributes,
  useState,
  useRef,
  useEffect,
} from 'react';
import {
  Form,
  FormControlProps,
  InputGroup,
  InputGroupProps,
} from 'react-bootstrap';
import { VerifyProps } from 'tradevan-ui/type';
import HelperText from '../Form/HelperText';

export interface CustomInputProps extends VerifyProps<string> {
  /** 欄位名稱(改為 required) */
  name: string;
  /** 開頭裝飾，可放入字串、HTML Tag、Icon */
  startAdornment?: ReactNode;
  /** 開頭裝飾預設在 <InputGroup.Text /> 裡，customStartAdornment: true 時將移除 <InputGroup.Text /> */
  customStartAdornment?: boolean;
  /** 結尾裝飾，可放入字串、HTML Tag、Icon */
  endAdornment?: ReactNode;
  /** 結尾裝飾預設在 <InputGroup.Text /> 裡，customStartAdornment: true 時將移除 <InputGroup.Text /> */
  customEndAdornment?: boolean;
  /** 最外層 <InputGroup /> props */
  inputGroupProps?: InputGroupProps;
}

export interface InputProps
  extends Omit<FormControlProps, 'type'>,
    Omit<
      InputHTMLAttributes<HTMLInputElement>,
      'name' | 'onChange' | 'value' | 'size'
    >,
    CustomInputProps {}

const Input = forwardRef(
  (props: InputProps, ref: ForwardedRef<HTMLInputElement>): JSX.Element => {
    const {
      name,
      value,
      onChange,
      startAdornment,
      customStartAdornment,
      endAdornment,
      customEndAdornment,
      type,
      error,
      helperText,
      verifyBeforeChange,
      verifiers = [],
      onMsgChange,
      inputGroupProps,
      ...other
    } = props;

    const [message, setMessage] = useState('');
    const [firstVerify, setFirstVerify] = useState(verifyBeforeChange);
    const localRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      setFirstVerify(verifyBeforeChange);
    }, [verifyBeforeChange]);

    useEffect(() => {
      // 始設畫面預設不 verify , 除非 verifyBeforeChange = true
      if (!firstVerify) {
        setMessage('');
        return;
      }

      // 驗使用者設定之 verifier, 不驗空值(給 HTML required 驗)
      if (value && verifiers.length > 0) {
        const veriObj = verifiers.find((obj) => obj.func(value as string));
        if (veriObj) {
          setMessage(veriObj.text);
          return;
        }
      }

      // 驗 HTML validation
      if (localRef.current && localRef.current.validationMessage.length > 0) {
        setMessage(localRef.current.validationMessage);
        return;
      }

      // 先前檢驗都 pass
      setMessage('');
    }, [value, firstVerify]);

    useEffect(() => {
      if (onMsgChange) onMsgChange({ name, value: message });
    }, [message]);

    const localError = message.length > 0 || error;
    const localHelperText = message || helperText;
    return (
      <>
        <InputGroup hasValidation={localError} {...inputGroupProps}>
          {startAdornment && (
            <InputGroup.Prepend>
              {customStartAdornment ? (
                startAdornment
              ) : (
                <InputGroup.Text>{startAdornment}</InputGroup.Text>
              )}
            </InputGroup.Prepend>
          )}
          <Form.Control
            ref={(ele: HTMLInputElement) => {
              localRef.current = ele;
              if (ref) {
                if (typeof ref === 'function') {
                  ref(ele);
                } else if (typeof ref === typeof localRef) {
                  // eslint-disable-next-line no-param-reassign
                  ref.current = ele;
                }
              }
            }}
            isInvalid={localError}
            onChange={(e) => {
              setFirstVerify(true);
              if (onChange) onChange(e);
            }}
            {...{ name, value, type, ...other }}
          />
          {endAdornment && (
            <InputGroup.Append>
              {customEndAdornment ? (
                endAdornment
              ) : (
                <InputGroup.Text>{endAdornment}</InputGroup.Text>
              )}
            </InputGroup.Append>
          )}
        </InputGroup>
        <HelperText error={localError} helperText={localHelperText} />
      </>
    );
  },
);

Input.displayName = 'Input';
export default Input;
